import { ThemeEvents } from '@theme/events';

class CartDrawerRecommendations extends HTMLElement {
  #loadedProductId = null;
  #cartDrawer = null;
  #carouselCleanup = null;

  connectedCallback() {
    this.#cartDrawer = this.closest('cart-drawer-component');
    this.#cartDrawer?.addEventListener('dialog:open', this.handleDialogOpen);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.#cartDrawer?.removeEventListener('dialog:open', this.handleDialogOpen);
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.#carouselCleanup?.();
    this.#carouselCleanup = null;
  }

  handleDialogOpen = () => {
    this.loadRecommendations({ force: true });
  };

  handleCartUpdate = () => {
    this.loadRecommendations({ force: true });
  };

  async loadRecommendations({ force = false } = {}) {
    const container = this.querySelector('[data-recommendations-list]');
    const url = this.dataset.url;
    const limit = this.dataset.limit || '4';
    const intent = this.dataset.intent || 'related';

    if (!url || !container) {
      this.setAttribute('hidden', '');
      return;
    }

    const productId = await this.getProductId();

    if (!productId) {
      this.setAttribute('hidden', '');
      return;
    }

    if (!force && this.#loadedProductId === productId) {
      this.removeAttribute('hidden');
      return;
    }

    try {
      const response = await fetch(`${url}?product_id=${productId}&limit=${limit}&intent=${intent}`);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const data = await response.json();
      const products = data.products || [];

      if (!products.length) {
        this.setAttribute('hidden', '');
        return;
      }

      container.innerHTML = products
        .map((product) => this.renderProduct(product))
        .join('');
      this.#loadedProductId = productId;
      this.removeAttribute('hidden');
      this.setupCarousel();
    } catch (error) {
      console.error('Cart drawer recommendations error:', error);
      this.setAttribute('hidden', '');
    }
  }

  async getProductId() {
    if (this.dataset.productId) {
      return this.dataset.productId;
    }

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;

      const cart = await response.json();
      const firstItem = cart.items?.[0];

      if (!firstItem?.product_id) return null;

      this.dataset.productId = String(firstItem.product_id);
      return this.dataset.productId;
    } catch (error) {
      console.error('Cart drawer recommendations error:', error);
      return null;
    }
  }

  setupCarousel() {
    this.#carouselCleanup?.();
    this.#carouselCleanup = null;

    const carousel = this.querySelector('[data-recommendations-list]');
    const prevButton = this.querySelector('[data-recommendations-prev]');
    const nextButton = this.querySelector('[data-recommendations-next]');

    if (!(carousel instanceof HTMLElement) || !(prevButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) {
      return;
    }

    const updateButtons = () => {
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      prevButton.disabled = carousel.scrollLeft <= 0;
      nextButton.disabled = carousel.scrollLeft >= maxScroll - 1;
    };

    const onPrev = () => this.scrollCarousel(carousel, -1);
    const onNext = () => this.scrollCarousel(carousel, 1);

    prevButton.addEventListener('click', onPrev);
    nextButton.addEventListener('click', onNext);
    carousel.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);

    updateButtons();

    this.#carouselCleanup = () => {
      prevButton.removeEventListener('click', onPrev);
      nextButton.removeEventListener('click', onNext);
      carousel.removeEventListener('scroll', updateButtons);
      window.removeEventListener('resize', updateButtons);
    };
  }

  scrollCarousel(carousel, direction) {
    const scrollAmount = this.getScrollAmount(carousel);
    carousel.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
  }

  getScrollAmount(carousel) {
    const firstItem = carousel.querySelector('.cart-drawer-recommendations__item');
    if (!(firstItem instanceof HTMLElement)) {
      return carousel.clientWidth;
    }

    const styles = getComputedStyle(carousel);
    const columnGap = parseFloat(styles.columnGap || styles.gap || '0');
    return firstItem.getBoundingClientRect().width + columnGap;
  }

  renderProduct(product) {
    const imageUrl = this.getImageUrl(product);
    const productTitle = this.escapeHtml(product.title || '');
    const productUrl = product.url || '#';
    const optionsCount = Array.isArray(product.options) ? product.options.length : product.options?.length || 1;
    const addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';

    return `
      <article class="cart-drawer-recommendations__item" role="listitem">
        <product-card class="cart-drawer-recommendations__card">
          <a class="cart-drawer-recommendations__image-link" href="${productUrl}" ref="productCardLink">
            <span class="cart-drawer-recommendations__image-wrapper">
              ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" loading="lazy">` : ''}
            </span>
          </a>
          <div class="cart-drawer-recommendations__info">
            <a class="cart-drawer-recommendations__title-link" href="${productUrl}">
              <span class="cart-drawer-recommendations__title">${productTitle}</span>
            </a>
            <quick-add-component
              class="cart-drawer-recommendations__quick-add"
              ref="quickAdd"
              data-product-title="${productTitle}"
              data-product-options-count="${optionsCount}"
              data-quick-add-button="choose"
            >
              <button
                class="button button--primary cart-drawer-recommendations__quick-add-button"
                type="button"
                name="add"
                on:click="quick-add-component/handleClick"
              >
                ${addToCartLabel}
              </button>
            </quick-add-component>
          </div>
        </product-card>
      </article>
    `;
  }

  getImageUrl(product) {
    const featuredImage = product.featured_image;
    const rawUrl = typeof featuredImage === 'string' ? featuredImage : featuredImage?.url;

    if (!rawUrl) return '';

    const normalizedUrl = rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl;
    const separator = normalizedUrl.includes('?') ? '&' : '?';

    return `${normalizedUrl}${separator}width=360`;
  }

  formatMoney(cents) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const formatMatch = moneyFormat.match(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator|amount_with_apostrophe_separator)\s*\}\}/);
    const formatType = formatMatch ? formatMatch[1] : 'amount';

    let formattedValue = '';
    switch (formatType) {
      case 'amount_no_decimals':
        formattedValue = this.formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        formattedValue = this.formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = this.formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_apostrophe_separator':
        formattedValue = this.formatWithDelimiters(cents, 2, '.', "'");
        break;
      default:
        formattedValue = this.formatWithDelimiters(cents, 2, '.', ',');
        break;
    }

    return moneyFormat.replace(formatMatch?.[0] || '{{amount}}', formattedValue);
  }

  formatWithDelimiters(cents, precision = 2, decimal = '.', thousands = ',') {
    if (cents == null || isNaN(Number(cents))) return '';

    const number = (Number(cents) / 100).toFixed(precision);
    const [dollars, centsPart] = number.split('.');
    const dollarsWithDelimiters = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return centsPart ? `${dollarsWithDelimiters}${decimal}${centsPart}` : dollarsWithDelimiters;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
